// ═══════════════════════════════════════════════════════════════════════════
// HEADER — corps de la page « Mon compte » (rendu pur)
// Modale jusqu'au 2026-08-24, page `#my-account` depuis. Le rendu n'a pas
// changé : seul le conteneur diffère (cf. `header.ts > renderMyAccountPage`).
// Extrait de header.ts (découpe god-function `openMyAccountModal` 2026-07) :
// construction PURE du corps de la popup (consentement IA, crédits, notifications,
// relances comités, mascotte, mot de passe + onglet sondage). Aucun état de
// closure — toutes les entrées (immuables au moment du rendu) via `opts`.
// Comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t, fmtDate, trigramHtml.

import type { HdrUser } from './header.js';

export interface MyAccountBodyOpts {
  consentChecked: boolean;
  consentEditable: boolean;
  consentIsOrg: boolean;
  /** Provider IA actif ('mistral' UE / 'anthropic' US) : sélectionne le libellé. */
  consentProvider: string;
  canBuyCredits: boolean;
  purchaseStep: number;
  enabled: boolean;
  lvl: number;
  committeeAlertsEnabled: boolean;
  mascotEnabled: boolean;
  /** HTML de l'onglet sondage (rendu par `_surveyTabHtml()` côté header). */
  surveyTabHtml: string;
}

/** Corps HTML de la page « Mon compte » (onglets compte + sondage). */
export function buildMyAccountBody(opts: MyAccountBodyOpts): string {
  const {
    consentChecked,
    consentEditable,
    consentIsOrg,
    consentProvider,
    canBuyCredits,
    purchaseStep,
    enabled,
    lvl,
    committeeAlertsEnabled,
    mascotEnabled,
    surveyTabHtml,
  } = opts;

  // Libellés de consentement dépendants du provider actif : sous Mistral (UE)
  // plus de transfert hors UE, donc un texte différent. Clés i18n suffixées
  // .mistral / .anthropic (cf. i18n/fr.json).
  const consentSuffix = consentProvider === 'anthropic' ? 'anthropic' : 'mistral';

  const lvlOpt = (n: number): string =>
    `<option value="${n}"${n === lvl ? ' selected' : ''}>${esc(t('myAccount.notif.level' + n))}</option>`;

  const sectionStyle =
    'border:1px solid var(--border);border-radius:var(--radius);padding:14px 16px;margin-bottom:14px;background:var(--bg2)';
  const sectionTitleStyle =
    'font-size:11px;text-transform:uppercase;letter-spacing:.07em;color:var(--text3);font-weight:700;margin-bottom:10px';

  return `
    <div class="ma-tabs">
      <button type="button" class="ma-tab is-active" data-tab="account">${esc(t('myAccount.tab.account'))}</button>
      <button type="button" class="ma-tab" data-tab="survey">${esc(t('myAccount.tab.survey'))}</button>
    </div>
    <div class="ma-pane" data-pane="account">
    <section style="${sectionStyle}">
      <div style="${sectionTitleStyle}">${esc(t('myAccount.section.iaConsent'))}</div>
      <div style="font-size:13px;color:var(--text3);margin-bottom:12px;line-height:1.5">
        ${esc(t('myAccount.iaConsent.intro.' + consentSuffix))}
      </div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:8px;${consentEditable ? 'cursor:pointer;' : ''}font-weight:500">
          <input type="checkbox" id="ma-ia-consent"${consentChecked ? ' checked' : ''}${consentEditable ? '' : ' disabled'}>
          <span>${esc(t('myAccount.iaConsent.checkboxLabel.' + consentSuffix))}</span>
        </label>
      </div>
      ${consentIsOrg ? `<div style="font-size:12px;color:var(--text3);margin-top:8px;line-height:1.5">${esc(t('myAccount.iaConsent.orgNote'))}</div>` : ''}
      ${consentIsOrg && !consentEditable ? `<div style="font-size:12px;color:var(--text3);margin-top:6px;line-height:1.5">${esc(t('myAccount.iaConsent.readonlyDS'))}</div>` : ''}
      <div id="ma-ia-consent-err" style="color:var(--danger);font-size:12px;min-height:16px;margin-top:6px"></div>
      ${consentEditable ? `<div style="text-align:right;margin-top:8px"><button type="button" class="btn btn-primary btn-sm" id="ma-ia-consent-save">${esc(t('myAccount.iaConsent.btnSave'))}</button></div>` : ''}
    </section>

    ${
      canBuyCredits
        ? `<section style="${sectionStyle}">
      <div style="${sectionTitleStyle}">${esc(t('myAccount.section.iaCredits'))}</div>
      <div style="font-size:13px;color:var(--text3);margin-bottom:12px;line-height:1.5">${esc(t('myAccount.iaCredits.intro'))}</div>
      <div id="ma-ia-credits-err" style="color:var(--danger);font-size:12px;min-height:16px;margin-bottom:6px"></div>
      <button type="button" class="btn btn-secondary btn-sm" id="ma-ia-credits-buy"${purchaseStep > 0 ? '' : ' disabled'}>${esc(t('myAccount.iaCredits.buyBtn', { step: purchaseStep }))}</button>
    </section>`
        : ''
    }

    <section style="${sectionStyle}">
      <div style="${sectionTitleStyle}">${esc(t('myAccount.section.notifications'))}</div>
      <div style="font-size:13px;color:var(--text3);margin-bottom:12px;line-height:1.5">
        ${esc(t('myAccount.notif.intro'))}
      </div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-weight:500">
          <input type="checkbox" id="ma-notif-enabled"${enabled ? ' checked' : ''}>
          <span>${esc(t('myAccount.notif.enabledLabel'))}</span>
          <span class="help-tip" tabindex="0" role="img" aria-label="${esc(t('myAccount.notif.enabledHelp'))}" title="${esc(t('myAccount.notif.enabledHelp'))}">?</span>
        </label>
      </div>
      <div class="form-group" style="margin-top:12px">
        <label style="font-weight:500;display:inline-flex;align-items:center;gap:8px">
          <span>${esc(t('myAccount.notif.levelLabel'))}</span>
          <span class="help-tip" tabindex="0" role="img" aria-label="${esc(t('myAccount.notif.levelHelp'))}" title="${esc(t('myAccount.notif.levelHelp'))}">?</span>
        </label>
        <select id="ma-notif-level" style="width:100%;margin-top:4px">
          ${lvlOpt(0)}${lvlOpt(1)}${lvlOpt(2)}${lvlOpt(3)}${lvlOpt(4)}
        </select>
      </div>
      <div id="ma-notif-err" style="color:var(--danger);font-size:12px;min-height:16px;margin-top:6px"></div>
      <div style="text-align:right;margin-top:8px">
        <button type="button" class="btn btn-primary btn-sm" id="ma-notif-save">${esc(t('myAccount.notif.btnSave'))}</button>
      </div>
    </section>

    <section style="${sectionStyle}">
      <div style="${sectionTitleStyle}">${esc(t('myAccount.section.committeeAlerts'))}</div>
      <div style="font-size:13px;color:var(--text3);margin-bottom:12px;line-height:1.5">
        ${esc(t('myAccount.committeeAlerts.intro'))}
      </div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-weight:500">
          <input type="checkbox" id="ma-committee-alerts-enabled"${committeeAlertsEnabled ? ' checked' : ''}>
          <span>${esc(t('myAccount.committeeAlerts.enabledLabel'))}</span>
          <span class="help-tip" tabindex="0" role="img" aria-label="${esc(t('myAccount.committeeAlerts.enabledHelp'))}" title="${esc(t('myAccount.committeeAlerts.enabledHelp'))}">?</span>
        </label>
      </div>
      <div id="ma-committee-alerts-err" style="color:var(--danger);font-size:12px;min-height:16px;margin-top:6px"></div>
      <div style="text-align:right;margin-top:8px">
        <button type="button" class="btn btn-primary btn-sm" id="ma-committee-alerts-save">${esc(t('myAccount.committeeAlerts.btnSave'))}</button>
      </div>
    </section>

    <section style="${sectionStyle}">
      <div style="${sectionTitleStyle}">${esc(t('myAccount.section.mascot'))}</div>
      <div style="font-size:13px;color:var(--text3);margin-bottom:12px;line-height:1.5">
        ${esc(t('myAccount.mascot.intro'))}
      </div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-weight:500">
          <input type="checkbox" id="ma-mascot-enabled"${mascotEnabled ? ' checked' : ''}>
          <span>${esc(t('myAccount.mascot.enabledLabel'))}</span>
          <span class="help-tip" tabindex="0" role="img" aria-label="${esc(t('myAccount.mascot.enabledHelp'))}" title="${esc(t('myAccount.mascot.enabledHelp'))}">?</span>
        </label>
      </div>
      <div id="ma-mascot-err" style="color:var(--danger);font-size:12px;min-height:16px;margin-top:6px"></div>
      <div style="text-align:right;margin-top:8px">
        <button type="button" class="btn btn-primary btn-sm" id="ma-mascot-save">${esc(t('myAccount.mascot.btnSave'))}</button>
      </div>
    </section>

    <section style="${sectionStyle}">
      <div style="${sectionTitleStyle}">${esc(t('myAccount.section.password'))}</div>
      <div style="font-size:13px;color:var(--text3);margin-bottom:10px;line-height:1.5">
        ${esc(t('myAccount.password.intro'))}
      </div>
      <button type="button" class="btn btn-secondary" id="ma-password-btn">${esc(t('header.account.changePassword'))}</button>
    </section>
    </div>
    <div class="ma-pane" data-pane="survey" hidden>${surveyTabHtml}</div>
  `;
}

/** HTML du menu déroulant « compte » (identité, code client, groupes/équipes,
 * ligne locale masquée, hôte quotas, actions). Purement construit à partir des
 * arguments ; le câblage et le chargement des quotas restent côté header. */
export function buildAccountMenuHtml(
  u: HdrUser,
  groups: Array<{ name: string }>,
  teamNames: string[],
  canBugs: boolean,
  bugCount: number,
): string {
  // Liste verticale de chips (un item par ligne) dans la valeur d'une ligne
  // « Label : … » — pour Groupe(s) et Équipe(s) affichés les uns sous les autres.
  const chipsHtml = (arr: string[], empty: string): string =>
    arr.length
      ? `<span class="am-vlist">${arr.map((name) => `<span class="am-chip">${esc(name)}</span>`).join('')}</span>`
      : `<span class="am-empty">${empty}</span>`;

  // Ligne « Label : valeur » (label muet à gauche, valeur à droite).
  const infoRow = (label: string, valueHtml: string): string =>
    `<div class="am-info-row"><span class="am-info-label">${esc(label)} :</span><span class="am-info-value">${valueHtml}</span></div>`;

  return `
      <div class="am-identity">
        <div><strong>${esc(u.name || '')}</strong> ${trigramHtml(u, { extraStyle: 'color:var(--text3);margin-left:6px;font-size:12px', placeholder: '' })}</div>
        <div style="color:var(--text3);font-size:12px">${esc(u.role || '—')}${u.is_external ? ' · ' + esc(t('header.account.external')) : ''}</div>
        ${u.departure_date && u.departure_date !== '2100-12-31' ? `<div style="color:var(--text3);font-size:12px;margin-top:2px">${t('header.account.departure', { date: fmtDate(u.departure_date) })}</div>` : ''}
      </div>

      <div class="am-info">
        ${infoRow(
          t('header.account.codeClient'),
          u.code_client
            ? `<span class="am-chip am-chip-mono">${esc(u.code_client)}</span>`
            : `<span class="am-empty">${esc(t('header.account.codeClientEmpty'))}</span>`,
        )}
        ${infoRow(
          t('header.account.groups'),
          chipsHtml(
            groups.map((g) => g.name),
            esc(t('header.account.groupsEmpty')),
          ),
        )}
        ${infoRow(t('header.account.teams'), chipsHtml(teamNames, esc(t('header.account.teamsEmpty'))))}
      </div>

      <div class="am-section-label" id="am-locale-label" style="display:none">${esc(t('header.account.locale'))}</div>
      <div id="am-locale-row" style="display:none">
        <select id="am-locale-select" style="width:100%;padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg2);font-size:13px"></select>
      </div>

      <div class="am-section-label">Consommation IA</div>
      <div id="am-quotas-host" style="font-size:12px;color:var(--text3);font-style:italic">Chargement…</div>

      <div class="am-actions-block">
        <button class="btn btn-secondary am-cta" id="am-my-account">${esc(t('header.account.myAccount'))}</button>

        <div class="am-menu-list">
          <button type="button" class="am-menu-item" id="am-assistant">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9z"/><path d="M18.5 14.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z"/></svg>
            <span>${esc(t('header.account.assistant'))}</span>
          </button>
          ${
            canBugs
              ? `<button type="button" class="am-menu-item" id="am-bug">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m8 2 1.88 1.88"/><path d="M14.12 3.88 16 2"/><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"/><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"/><path d="M12 20v-9"/><path d="M6.53 9C4.6 8.8 3 7.1 3 5"/><path d="M6 13H2"/><path d="M3 21c0-2.1 1.7-3.9 3.8-4"/><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"/><path d="M22 13h-4"/><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"/></svg>
            <span>${esc(t('header.account.reportBug'))}</span>
            ${bugCount > 0 ? `<span class="am-menu-count">${bugCount > 99 ? '99+' : bugCount}</span>` : ''}
          </button>`
              : ''
          }
          <button type="button" class="am-menu-item" id="am-about">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <span>${esc(t('header.account.about'))}</span>
          </button>

          <div class="am-menu-sep" aria-hidden="true"></div>

          <button type="button" class="am-menu-item am-menu-item-danger" id="am-logout">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            <span>${esc(t('header.account.logout'))}</span>
          </button>
        </div>
      </div>
    `;
}
